export const Role = {
    CLIENT: 'client',
    OPERATOR: 'operator',
    ADMIN: 'admin',
};
/**
 * Fine-grained admin permissions. Format: `<entity>.<action>`. Admin role
 * bypasses every check; operators must have the specific permission listed
 * here for the corresponding endpoint to accept their request.
 *
 * The UI uses this catalog to render the permission checkboxes on
 * /users/:id, so adding an entry here automatically surfaces it in the
 * backoffice — keep ordering meaningful (entity, then action).
 */
export const ADMIN_PERMISSIONS = [
    'orders.view',
    'orders.edit',
    'transfers.view',
    'transfers.edit',
    'auth-files.view',
    'auth-files.edit',
    'variants.view',
    'variants.edit',
    'stations.view',
    'stations.edit',
    'users.view',
    'users.edit',
    'wallet.view',
    'wallet.edit',
    'wholesale.view',
    'wholesale.edit',
    'cupet.view',
    'cupet.edit',
    'gas.view',
    'gas.edit',
    'cron-runs.view',
    'audit.view',
];
export const ADMIN_PERMISSION_GROUPS = [
    { entity: 'orders', label: 'Órdenes', perms: ['orders.view', 'orders.edit'] },
    { entity: 'transfers', label: 'Transferencias', perms: ['transfers.view', 'transfers.edit'] },
    { entity: 'auth-files', label: 'Autorizos', perms: ['auth-files.view', 'auth-files.edit'] },
    { entity: 'variants', label: 'Combustibles', perms: ['variants.view', 'variants.edit'] },
    { entity: 'stations', label: 'Estaciones', perms: ['stations.view', 'stations.edit'] },
    { entity: 'users', label: 'Usuarios', perms: ['users.view', 'users.edit'] },
    { entity: 'wallet', label: 'Billetera (admin)', perms: ['wallet.view', 'wallet.edit'] },
    { entity: 'wholesale', label: 'Mayorista', perms: ['wholesale.view', 'wholesale.edit'] },
    { entity: 'cupet', label: 'Combustible CUPET', perms: ['cupet.view', 'cupet.edit'] },
    { entity: 'gas', label: 'Gas / Balitas', perms: ['gas.view', 'gas.edit'] },
    { entity: 'cron-runs', label: 'Crons', perms: ['cron-runs.view'] },
    { entity: 'audit', label: 'Auditoría', perms: ['audit.view'] },
];
export const FuelFamily = {
    GASOLINA: 'gasolina',
    DIESEL: 'diesel',
    EV: 'ev',
};
export const PaymentMethod = {
    WALLET: 'wallet',
    CARD: 'card',
    TRANSFER: 'transfer',
    /** Combination: part from wallet + remainder via card/transfer. */
    SPLIT: 'split',
};
export const OrderStatus = {
    /** Order in the user's cart — slot is reserved but no payment yet. */
    DRAFT: 'draft',
    PENDING_PAYMENT: 'pending_payment',
    PENDING_AUTH: 'pending_auth',
    CONFIRMED: 'confirmed',
    SURTIDO: 'surtido',
    CANCELLED: 'cancelled',
    EXPIRED: 'expired',
    /** Customer no-showed beyond the allowed reschedules — money is forfeit. */
    FORFEITED: 'forfeited',
};
export const RescheduleStatus = {
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
};
/** Customer gets 2 reschedule attempts before the 3rd no-show forfeits the order. */
export const RESCHEDULE_MAX_ATTEMPTS = 2;
export const WalletTxKind = {
    TOP_UP: 'top_up',
    DEBIT: 'debit',
    REFUND: 'refund',
    ADJUSTMENT: 'adjustment',
    /** Reserve funds for a pending order (no actual balance move yet). */
    HOLD: 'hold',
    /** Release a previously held amount (order cancelled before payment). */
    HOLD_RELEASE: 'hold_release',
    /** Convert a hold into a real debit (order paid & confirmed). */
    HOLD_SETTLE: 'hold_settle',
};
export const WalletStatus = {
    ACTIVE: 'active',
    FROZEN: 'frozen',
    CLOSED: 'closed',
};
export const HoldStatus = {
    ACTIVE: 'active',
    SETTLED: 'settled',
    RELEASED: 'released',
    EXPIRED: 'expired',
};
export const SlotAvailability = {
    AVAILABLE: 'Disponible',
    FEW: 'Pocos cupos',
    LAST: 'Última',
    FULL: 'Lleno',
};
export const QueueState = {
    NONE: 'Sin fila',
    SHORT: 'Corta',
    MEDIUM: 'Media',
    LONG: 'Larga',
};
export const AuthFileStatus = {
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
};
export const WholesaleContainerType = {
    IBC_25000: 'ibc_25000',
    ISO_23500: 'iso_23500',
};
export const WHOLESALE_CONTAINER_LITERS = {
    ibc_25000: 25000,
    iso_23500: 23500,
};
export const WHOLESALE_CONTAINER_LABEL = {
    ibc_25000: 'IBC 25 000 L',
    iso_23500: 'Isocontenedor 23 500 L',
};
export const WholesaleStatus = {
    DRAFT: 'draft',
    SUBMITTED: 'submitted',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    PAID: 'paid',
    DELIVERED: 'delivered',
    CANCELLED: 'cancelled',
};
export const WholesaleCustomerType = {
    MIPYME: 'mipyme',
    TCP: 'tcp',
};
export const WholesaleDocKind = {
    ID: 'id',
    LICENSE: 'license',
    REGISTRATION: 'registration',
    FISCAL_ADDRESS: 'fiscal_address',
    OTHER: 'other',
};
/**
 * Lifecycle status of a fuel allocation in the external CUPET fuel API.
 * Numeric values are CUPET's — sent verbatim in their Type-1 (status change)
 * notification. Do NOT renumber: the webhook maps these straight through.
 *   1 inserted · 2 in process · 3 provisioned (despachada) · 7 cancelled
 */
export const CupetAllocationStatus = {
    INSERTED: 1,
    IN_PROCESS: 2,
    PROVISIONED: 3,
    CANCELLED: 7,
};
export const CUPET_STATUS_LABEL = {
    1: 'Insertada',
    2: 'En proceso',
    3: 'Aprovisionada',
    7: 'Cancelada',
};
//# sourceMappingURL=enums.js.map